import { Component, effect, EventEmitter, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from '@sbd-doug-frontend/app/core/navigation/navigation.service';
import { Navigation } from '@sbd-doug-frontend/app/core/navigation/navigation.types';
import { environment } from '@sbd-doug-frontend/app/environments/environment';
import { Subject, takeUntil } from 'rxjs';
import {
    PersistentDrawerTab,
    PersistentDrawerTabsService
} from '@sbd-doug-frontend/app/layout/services/persistent-drawer-tabs.service';
import { DrawerRef } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-manager.service';
import { CIPXDrawerContentInterface } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-content.interface';
import { PersistentDrawerRegistryService } from '@sbd-doug-frontend/app/layout/services/persistent-drawer-registry.service';

type PersistentDrawerContentInstance = CIPXDrawerContentInterface<any> & {
    drawerClosed?: EventEmitter<void>;
    drawerCollapsed?: EventEmitter<void>;
};

@Component({
    selector     : 'modern-layout',
    templateUrl  : './modern.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class ModernLayoutComponent implements OnInit, OnDestroy
{
    isScreenSmall: boolean;
    navigation: Navigation;
    readonly maxVisibleDesktopTabs = 4;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _openedDrawerTabId: string | null = null;
    private _activeDrawerRef: DrawerRef<CIPXDrawerContentInterface<any>> | null = null;

    homeUrl = `${environment.dougV2BaseUrl}/admin/dashboard`;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _persistentDrawerTabsService: PersistentDrawerTabsService,
        private _persistentDrawerRegistryService: PersistentDrawerRegistryService,
        private _fuseNavigationService: FuseNavigationService
    )
    {
        effect(() => {
            this._persistentDrawerTabsService.activeTabId();
            this._persistentDrawerTabsService.tabs();
            this._syncActiveDrawerFromTabs();
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        this._syncActiveDrawerFromTabs();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        this._closeActiveDrawer();
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    drawerTabs(): PersistentDrawerTab[] {
        return this._persistentDrawerTabsService.tabs();
    }

    desktopVisibleDrawerTabs(): PersistentDrawerTab[] {
        return this.drawerTabs().slice(0, this.maxVisibleDesktopTabs);
    }

    overflowDrawerTabs(): PersistentDrawerTab[] {
        return this.drawerTabs().slice(this.maxVisibleDesktopTabs);
    }

    mobileActiveDrawerTab(): PersistentDrawerTab | null {
        const activeId = this._persistentDrawerTabsService.activeTabId();
        return this.drawerTabs().find((tab) => tab.id === activeId) || null;
    }

    mobilePrimaryDrawerTab(): PersistentDrawerTab | null {
        return this.drawerTabs()[0] || null;
    }

    mobileOverflowDrawerTabs(): PersistentDrawerTab[] {
        return this.drawerTabs().slice(1);
    }

    isDrawerTabActive(tabId: string): boolean {
        return this._persistentDrawerTabsService.activeTabId() === tabId;
    }

    onDrawerTabClicked(tab: PersistentDrawerTab): void {
        if (this.isDrawerTabActive(tab.id)) {
            this._collapseActiveDrawer();
            return;
        }

        this._persistentDrawerTabsService.setActiveTab(tab.id);
    }

    onDrawerTabMenuSelect(tab: PersistentDrawerTab): void {
        this._persistentDrawerTabsService.setActiveTab(tab.id);
    }

    onDrawerTabRemove(tabId: string, event: MouseEvent): void {
        event.stopPropagation();
        if (this._openedDrawerTabId === tabId) {
            this._closeActiveDrawer();
        }
        this._persistentDrawerTabsService.removeTab(tabId);
    }

    private _syncActiveDrawerFromTabs(): void {
        const activeTabId = this._persistentDrawerTabsService.activeTabId();
        if (!activeTabId) {
            this._closeActiveDrawer();
            return;
        }

        const activeTab = this._persistentDrawerTabsService.tabs().find((tab) => tab.id === activeTabId);
        if (!activeTab) {
            this._closeActiveDrawer();
            return;
        }

        if (this._openedDrawerTabId === activeTab.id && this._activeDrawerRef?.componentInstance) {
            return;
        }

        this._collapseActiveDrawer(false);
        this._openDrawerForTab(activeTab);
    }

    private _openDrawerForTab(tab: PersistentDrawerTab): void {
        const drawerRef = this._persistentDrawerRegistryService.open(tab);
        if (!drawerRef) {
            if (this._persistentDrawerTabsService.activeTabId() === tab.id) {
                this._persistentDrawerTabsService.setActiveTab(null);
            }
            return;
        }

        this._activeDrawerRef = drawerRef;
        this._openedDrawerTabId = tab.id;
        this._attachDrawerLifecycle(tab, drawerRef.componentInstance as PersistentDrawerContentInstance);
    }

    private _attachDrawerLifecycle(tab: PersistentDrawerTab, componentInstance: PersistentDrawerContentInstance): void {
        const drawerClosedSubscription = componentInstance.drawerClosed?.subscribe(() => {
            this._openedDrawerTabId = null;
            this._activeDrawerRef = null;
            this._persistentDrawerTabsService.removeTab(tab.id);
            drawerClosedSubscription?.unsubscribe();
        });

        const drawerCollapsedSubscription = componentInstance.drawerCollapsed?.subscribe(() => {
            this._openedDrawerTabId = null;
            this._activeDrawerRef = null;
            if (this._persistentDrawerTabsService.activeTabId() === tab.id) {
                this._persistentDrawerTabsService.setActiveTab(null);
            }
            drawerCollapsedSubscription?.unsubscribe();
        });
    }

    private _closeActiveDrawer(): void {
        const drawerRef = this._activeDrawerRef;
        this._activeDrawerRef = null;
        this._openedDrawerTabId = null;
        if (drawerRef?.componentInstance) {
            drawerRef.close();
        }
    }

    private _collapseActiveDrawer(clearActiveTab = true): void {
        const drawerRef = this._activeDrawerRef;
        if (!drawerRef?.componentInstance) {
            if (clearActiveTab) {
                this._persistentDrawerTabsService.setActiveTab(null);
            }
            return;
        }

        drawerRef.componentInstance.onCollapseClick?.();
        this._closeActiveDrawer();
    }
}
